import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { siteUrl } from '@/lib/site';
import { MAIN_SITE_URL } from '@/mocks/categories';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mt-10 mb-4">{children}</h2>;
}

function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-stone-700 text-sm md:text-base">
          <i className="ri-check-double-line text-emerald-600 w-5 h-5 flex items-center justify-center mt-0.5" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Article() {
  const { t } = useTranslation();

  const coverImage =
    'https://readdy.ai/api/search-image?query=Clean%20minimalist%20flat%20lay%20of%20a%20notebook%20with%20a%20hand%20writing%20a%20checklist%20next%20to%20a%20laptop%20and%20coffee%20on%20a%20soft%20cream%20desk%2C%20fresh%20airy%20aesthetic%2C%20soft%20natural%20lighting%2C%20high%20detail%20modern%20European%20editorial%20style&width=1200&height=600&seq=article-cover-01&orientation=landscape';

  const articleData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: t('art.title'),
    description: t('art.subtitle'),
    image: coverImage,
    author: { '@type': 'Organization', name: 'USFans Editorial Team' },
    publisher: { '@type': 'Organization', name: 'USFans Spreadsheet', url: siteUrl('/') },
    datePublished: '2026-08-22',
    dateModified: '2026-08-22',
    mainEntityOfPage: siteUrl('/usfans-spreadsheet-beginner-guide'),
  };

  const faqData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('art.faqQ1'), acceptedAnswer: { '@type': 'Answer', text: t('art.faqA1') } },
      { '@type': 'Question', name: t('art.faqQ2'), acceptedAnswer: { '@type': 'Answer', text: t('art.faqA2') } },
      { '@type': 'Question', name: t('art.faqQ3'), acceptedAnswer: { '@type': 'Answer', text: t('art.faqA3') } },
      { '@type': 'Question', name: t('art.faqQ4'), acceptedAnswer: { '@type': 'Answer', text: t('art.faqA4') } },
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream text-stone-900">
      <JsonLd data={articleData} />
      <JsonLd data={faqData} />
      <Header />
      <main className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-14">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-500 flex-wrap">
          <Link to="/" className="hover:text-emerald-700 cursor-pointer whitespace-nowrap">
            {t('art.breadcrumbHome')}
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-stone-400 whitespace-nowrap">{t('art.breadcrumbGuides')}</span>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-emerald-700 font-medium whitespace-nowrap">{t('art.breadcrumbCurrent')}</span>
        </nav>

        {/* Header */}
        <header className="mt-6">
          <span className="inline-block rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
            {t('home.navGuides')}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-stone-900 leading-tight">
            {t('art.title')}
          </h1>
          <p className="mt-4 text-base md:text-lg text-stone-600">{t('art.subtitle')}</p>
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-stone-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
              {t('art.writtenBy')}
            </span>
            <span className="hidden sm:inline text-stone-300">•</span>
            <span>{t('art.updated')}</span>
          </div>
        </header>

        {/* Cover */}
        <div className="mt-8 rounded-lg overflow-hidden border border-stone-200 bg-white">
          <img
            src={coverImage}
            alt="USFans Spreadsheet Beginner Guide cover"
            title="USFans Spreadsheet Beginner Guide"
            className="w-full h-52 md:h-72 object-cover object-top"
          />
        </div>

        {/* Body */}
        <article className="mt-8">
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.intro')}</p>

          <SectionHeading>{t('art.h1')}</SectionHeading>
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p1a')}</p>
          <p className="mt-4 text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p1b')}</p>

          <SectionHeading>{t('art.h2')}</SectionHeading>
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p2a')}</p>
          <div className="mt-4">
            <CheckList items={[t('art.l21'), t('art.l22'), t('art.l23'), t('art.l24')]} />
          </div>

          <SectionHeading>{t('art.h3')}</SectionHeading>
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p3a')}</p>
          <p className="mt-4 text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p3b')}</p>

          <SectionHeading>{t('art.h4')}</SectionHeading>
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p4a')}</p>
          <div className="mt-4">
            <CheckList items={[t('art.l41'), t('art.l42'), t('art.l43'), t('art.l44')]} />
          </div>

          <SectionHeading>{t('art.h5')}</SectionHeading>
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p5a')}</p>
          <p className="mt-4 text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p5b')}</p>

          <SectionHeading>{t('art.h6')}</SectionHeading>
          <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.p6a')}</p>

          <SectionHeading>{t('art.h7')}</SectionHeading>
          <div className="mt-4">
            <CheckList items={[t('art.l71'), t('art.l72'), t('art.l73'), t('art.l74'), t('art.l75')]} />
          </div>

          <SectionHeading>{t('art.h8')}</SectionHeading>
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <CheckList
              items={[t('art.l81'), t('art.l82'), t('art.l83'), t('art.l84'), t('art.l85'), t('art.l86'), t('art.l87'), t('art.l88')]}
            />
          </div>

          {/* FAQ */}
          <SectionHeading>{t('home.faqTitle')}</SectionHeading>
          <div className="space-y-3">
            {[
              { q: t('art.faqQ1'), a: t('art.faqA1') },
              { q: t('art.faqQ2'), a: t('art.faqA2') },
              { q: t('art.faqQ3'), a: t('art.faqA3') },
              { q: t('art.faqQ4'), a: t('art.faqA4') },
            ].map((f, i) => (
              <div key={i} className="rounded-lg border border-stone-200 bg-white p-5">
                <h3 className="font-heading font-semibold text-stone-900 text-sm md:text-base">{f.q}</h3>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          {/* Conclusion */}
          <div className="mt-10 rounded-lg bg-emerald-50 border border-emerald-100 p-6">
            <p className="text-sm md:text-base text-stone-700 leading-relaxed">{t('art.conclusion')}</p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 text-center whitespace-nowrap cursor-pointer"
              >
                {t('art.ctaHome')}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="rounded-md border border-emerald-300 px-5 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 text-center whitespace-nowrap cursor-pointer"
              >
                {t('art.ctaSpreadsheet')}
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}